import { makeAutoObservable } from "mobx";
import { TableColumnsType } from "antd";
import {
  combineChecked,
  groupChecked,
  GroupChecked,
} from "src/common/groupChecked";
import { changeSetItem, toggleSetItem } from "src/common/toggleSetItem";
import { ZUid } from "src/types/zUid";
import { onError } from "src/common/onError";
import {
  applyOrderStd,
  CtrlColumns,
  CtrlResizable,
} from "src/components/tables/TableStore";
import { ColumnKey, ColumnPoor } from "src/components/tables/ColumnPoor";
import { debounce, DebounceCounter } from "src/common/debounce";
import { Key } from "react";
import { testStrPart } from "src/components/tables/clientFiltering";
import { buildPlanning2Columns } from "../buildPlanning2Columns";
import { ZPlanning2Row } from "../../ZPlanning2Row";
import {
  deletePlanning2Tasks,
  loadPlanning2,
  savePlanning2TableSettings,
} from "../../apiPlanning2";
import { ZPlanning2TableSettings } from "../../ZPlanning2TableSettings";
import { Planning2DatesStore } from "../Planning2Dates/Planning2DatesStore";
import { Planning2Column, planning2MinColumnWidth } from "./Planning2Column";

export type Pl2CurrentTask = {
  id: string;
  name: string;
};

export class Planning2TableStore {
  constructor(
    public readonly objectServiceId: ZUid,
    public readonly entityId: ZUid,
    settings: ZPlanning2TableSettings,
    public readonly datesStore: Planning2DatesStore,
  ) {
    this.setSettings(settings);
    this.setRawFixedColumns(settings.fixedColumns ?? this.rawFixedColumns);
    this.reload()
      .then(() => this.expandAll())
      .catch(onError);
    makeAutoObservable(this);
  }

  rows: ZPlanning2Row[] = [];

  setRows(newRows: ZPlanning2Row[]) {
    this.rows = newRows;
  }

  get visibleRows(): ZPlanning2Row[] {
    const { filter, rows } = this;
    if (!filter) {
      return rows;
    }
    const result: ZPlanning2Row[] = [];
    const isVisibleRow = (row: ZPlanning2Row) =>
      testStrPart<ZPlanning2Row>("name", true)(row, filter);
    rows.forEach((row) => {
      if (isVisibleRow(row)) {
        result.push(row);
      } else if (row.kind === "group") {
        const group = { ...row };
        group.children = row.children.filter(isVisibleRow);
        if (group.children.length > 0) {
          result.push(group);
        }
      }
    });
    return result;
  }

  filter = "";

  setFilter(newFilter: string | null) {
    this.filter = newFilter ?? "";
  }

  loading = false;

  setLoading(flag: boolean) {
    this.loading = flag;
  }

  async reload() {
    this.setLoading(true);
    try {
      const { tasks, startDate, endDate } = await loadPlanning2(
        this.objectServiceId,
        this.entityId,
      );
      this.datesStore.setInitialDates(startDate, endDate);
      this.setRows(tasks);
    } finally {
      this.setLoading(false);
    }
  }

  get allTasks(): ZPlanning2Row[] {
    return this.rows.flatMap((row) =>
      row.kind === "task" ? [row] : row.children,
    );
  }

  get groupsCount(): number {
    return this.rows.reduce(
      (acc, { kind }) => (kind === "group" ? acc + 1 : acc),
      0,
    );
  }

  // Expand
  expanded = new Set<string>();

  get expandedRowKeys(): string[] {
    return Array.from(this.expanded);
  }

  get canExpandAll(): boolean {
    return this.expanded.size < this.groupsCount;
  }

  expandAll() {
    this.expanded = new Set(
      this.rows.filter((row) => row.kind === "group").map(({ id }) => id),
    );
  }

  get canCollapseAll(): boolean {
    return this.expanded.size > 0;
  }

  collapseAll() {
    this.expanded.clear();
  }

  changeExpand(key: string, expanded: boolean) {
    changeSetItem(this.expanded, key, expanded);
  }

  // Выделение строк (групп и задач)

  selectedTasks = new Set<string>();

  clearSelected() {
    this.selectedTasks.clear();
  }

  get selectedTasksList() {
    const { allTasks, selectedTasks } = this;
    return allTasks.filter(({ id }) => selectedTasks.has(id));
  }

  get selectedGroups(): Record<string, GroupChecked> {
    const result: Record<string, GroupChecked> = {};
    this.rows.forEach((row) => {
      if (row.kind === "group") {
        result[row.id] =
          groupChecked(row.children, (task) =>
            this.selectedTasks.has(task.id),
          ) ?? "none";
      }
    });
    return result;
  }

  get selectedAll(): GroupChecked {
    return (
      this.rows.reduce(
        (
          acc: GroupChecked | undefined,
          row: ZPlanning2Row,
        ): GroupChecked | undefined => {
          let curValue: GroupChecked | undefined;
          if (row.kind === "group") {
            curValue = this.selectedGroups[row.id];
          } else {
            curValue = this.selectedTasks.has(row.id) ? "all" : "none";
          }
          return combineChecked(acc, curValue) ?? "none";
        },
        undefined,
      ) ?? "none"
    );
  }

  toggleTaskSelection(id: string) {
    toggleSetItem(this.selectedTasks, id);
  }

  toggleGroupSelection(groupId: string) {
    const row = this.rows.find(({ id }) => id === groupId);
    if (row?.kind === "group") {
      if (this.selectedGroups[groupId] === "all") {
        row.children.forEach(({ id }) => this.selectedTasks.delete(id));
      } else {
        row.children.forEach(({ id }) => this.selectedTasks.add(id));
      }
    }
  }

  toggleAllSelection() {
    if (this.selectedAll === "all") {
      this.selectedTasks.clear();
    } else {
      this.selectedTasks = new Set(this.allTasks.map(({ id }) => id));
    }
  }

  // Удаление выделенных задач
  async deleteSelectedTasks() {
    const taskIds = this.selectedTasksList.map(({ id }) => id);
    await deletePlanning2Tasks(taskIds);
    await this.reload();
    this.clearSelected();
  }

  currentTask: Pl2CurrentTask | null = null;

  setCurrentTask(task: Pl2CurrentTask | null) {
    this.currentTask = task;
  }

  get allColumns(): Planning2Column[] {
    const store = this;
    return buildPlanning2Columns({
      onTask: ({ id, name }) => {
        store.setCurrentTask({ id, name });
      },
    });
  }

  get finalColumns(): Planning2Column[] {
    const { hiddenColumns, settings } = this;
    const columnWidths = settings.columnWidths ?? {};
    const columns: Planning2Column[] = this.allColumns
      .filter(({ key }) => !hiddenColumns.has(String(key)))
      .map((column) => ({
        ...column,
        width: Math.max(
          columnWidths[column.key] || column.width,
          planning2MinColumnWidth,
        ),
      }));
    const orders = new Map<Key, number>(
      this.settings.columnsOrder?.map((key, i) => [key, i]),
    );
    // имя должно быть первым. Потому что к нему привязана кнопка раскрытия
    orders.set("name", -1);
    const weight = (key?: Key) =>
      orders.get(key ?? "") ?? Number.MAX_SAFE_INTEGER;
    columns.sort((a, b) => weight(a.key) - weight(b.key));
    return columns;
  }

  // Settings

  settings: ZPlanning2TableSettings = {};

  setSettings(newSettings: ZPlanning2TableSettings) {
    this.settings = newSettings;
  }

  updateSettings(newSettings: Partial<ZPlanning2TableSettings>) {
    this.setSettings({ ...this.settings, ...newSettings });
    this.saveSettings();
  }

  saveSettings() {
    debounce(saveSettingsCounter, 100, () => {
      savePlanning2TableSettings(this.settings).catch(onError);
    });
  }

  get hiddenColumns(): Set<string> {
    return new Set(this.settings.hiddenColumns);
  }

  rawFixedColumns = 0;

  setRawFixedColumns(count: number) {
    this.rawFixedColumns = count;
  }

  changeRawFixedColumns(count: number) {
    this.setRawFixedColumns(count);
    this.updateSettings({ fixedColumns: count });
  }

  get extraFixedColumns(): number {
    const { ctrlColumns } = this;
    const sortedColumns = applyOrderStd(ctrlColumns, ctrlColumns.columns);
    const visible = (col: ColumnPoor) =>
      ctrlColumns.isColumnVisible(col.key) ? 1 : 0;

    return sortedColumns
      .slice(0, this.rawFixedColumns)
      .reduce((sum, col) => sum + visible(col), 0);
  }

  get ctrlColumns(): CtrlColumns {
    const main = this;
    const cvtColumns = (src: TableColumnsType<ZPlanning2Row>): ColumnPoor[] =>
      src.map(({ key, title }) => ({
        key: String(key),
        title: typeof title === "string" ? title : undefined,
      }));
    return {
      get columns(): ColumnPoor[] {
        return cvtColumns(main.allColumns.slice(1));
      },
      get finalColumns(): ColumnPoor[] {
        return cvtColumns(main.finalColumns);
      },
      get columnOrder(): string[] {
        return (
          main.settings.columnsOrder ??
          main.allColumns.map(({ key }) => String(key))
        );
      },
      isColumnVisible(key: ColumnKey): boolean {
        return !main.settings.hiddenColumns?.includes(key);
      },
      setColumnVisible(key: ColumnKey, visible: boolean): void {
        const hiddenSet = new Set(main.hiddenColumns);
        changeSetItem(hiddenSet, key, !visible);
        main.updateSettings({ hiddenColumns: Array.from(hiddenSet) });
      },
      canColumnHide(key: ColumnKey): boolean {
        return key !== "name";
      },
      setColumnOrder(columnsOrder: string[]): void {
        main.updateSettings({ columnsOrder });
      },
      get fixedLeftRaw(): number {
        return main.rawFixedColumns;
      },
      setFixedLeftRaw(count: number): void {
        main.changeRawFixedColumns(count);
      },
    };
  }

  get ctrlResizable(): CtrlResizable {
    const main = this;
    return {
      get columnWidths(): Record<string, number | string> {
        return main.settings.columnWidths ?? {};
      },
      resizableColumns: true,
      setColumnWidth(key: string, width?: number | string) {
        if (typeof width === "number") {
          const srcColumnWidths = main.settings.columnWidths;
          const columnWidths = { ...srcColumnWidths };
          columnWidths[key] = width;
          main.updateSettings({ columnWidths });
        }
      },
    };
  }
}

const saveSettingsCounter: DebounceCounter = {};
